import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useAuth0 } from "@auth0/auth0-react";
import { Loader, createStyles } from "@mantine/core";
import axios from "axios";
import { useQueryClient } from "react-query";

import { fetchCsrfTokenWithQuery } from "../../../utils/CsrfToken";
import { useAuthProfile } from "../../../utils/useAuthProfile";
import LoginCard from "../components/LoginCard";
import LoginPageWrapper from "../components/LoginPageWrapper";
import { SUPPORT_EMAIL } from "../components/constants";
import SessionTimeoutManager from "../../../utils/SessionTimeoutManager";

const AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;

const useStyles = createStyles(() => ({
  center: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  subTitle: {
    padding: "0px 12px",
    marginBottom: "24px",
  },
  description: {
    maxWidth: "330px",
    marginBottom: "24px",
    textAlign: "center",
  },
  email: {
    marginBottom: "0px",
  },
}));

const LoginLoadingCard = (): JSX.Element => {
  const { classes } = useStyles();

  return (
    <LoginCard className={classes.center}>
      <Loader size="lg" />
    </LoginCard>
  );
};

const LoginErrorCard = (): JSX.Element => {
  const { classes } = useStyles();

  return (
    <LoginCard className={classes.center}>
      <LoginCard.Logo />
      <LoginCard.Title>Oops!</LoginCard.Title>
      <LoginCard.SubTitle className={classes.subTitle}>
        Something went wrong and we couldn&apos;t log you in.
      </LoginCard.SubTitle>
      <LoginCard.Description className={classes.description}>
        You won&apos;t be able to continue right now - please reach out to our
        support team so we can help you get back in.
      </LoginCard.Description>
      <LoginCard.SubTitle className={classes.email}>
        Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
      </LoginCard.SubTitle>
    </LoginCard>
  );
};

const Auth0CallbackPage: React.FC = () => {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently } =
    useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasLoginFailed, setHasLoginFailed] = useState(false);
  const [, setAuthProfile] = useAuthProfile();
  const queryClient = useQueryClient();

  const urlParams = new URLSearchParams(location.search);
  const auth0ErrorCode = urlParams.get("error");
  const auth0ErrorDescription = urlParams.get("error_description");

  useEffect(() => {
    const handleDjangoLogin = async (): Promise<void> => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: AUDIENCE,
          },
        });

        // Ensure CSRF cookie is set before making the login request
        await fetchCsrfTokenWithQuery(queryClient);

        const { data } = await axios.post(
          "/auth/auth0/login/",
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        setAuthProfile(data);
        SessionTimeoutManager.start();
        navigate("/home");
      } catch (e: any) {
        setHasLoginFailed(true);
      }
    };

    if (auth0ErrorCode) {
      setHasLoginFailed(true);
      // eslint-disable-next-line no-console
      console.error(auth0ErrorCode, auth0ErrorDescription);
      return;
    }

    if (!isLoading && isAuthenticated && user) {
      handleDjangoLogin();
    }
  }, [
    isLoading,
    isAuthenticated,
    user,
    navigate,
    location.search,
    queryClient,
  ]);

  return (
    <LoginPageWrapper>
      {hasLoginFailed ? <LoginErrorCard /> : <LoginLoadingCard />}
    </LoginPageWrapper>
  );
};

export default Auth0CallbackPage;
